/**
 * AURA Settings Sync
 *
 * Replaces pure localStorage with a hybrid approach:
 * - When authenticated: persist to Supabase, sync across devices
 * - When anonymous: fall back to localStorage (development mode)
 *
 * Drop-in replacement for the localStorage calls in settings-interaction.ts
 */

import { createClient, SupabaseClient, RealtimeChannel } from '@supabase/supabase-js';
import { getAuthState, isAuthConfigured } from './auth';

// ============================================================================
// Types
// ============================================================================

type SettingsSection =
  | 'account'
  | 'organization'
  | 'datasets'
  | 'modes'
  | 'governance'
  | 'health';

interface SettingsStore {
  [key: string]: any;
}

// ============================================================================
// In-Memory Cache
// ============================================================================

const cache: Map<string, any> = new Map();
let cacheLoaded = false;

// ============================================================================
// Supabase Client
// ============================================================================

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || '';
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY || '';

let client: SupabaseClient | null = null;
let realtimeChannel: RealtimeChannel | null = null;

function getClient(): SupabaseClient | null {
  if (!SUPABASE_URL || !SUPABASE_ANON_KEY) return null;
  if (!client) {
    client = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);
  }
  return client;
}

// ============================================================================
// Load
// ============================================================================

/**
 * Load all settings. Called once on app init.
 * Merges remote (Supabase) settings with local cache.
 */
export async function loadAllSettings(): Promise<SettingsStore> {
  const store: SettingsStore = {};

  // Always load localStorage first (fast, always available)
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith('aura-setting-')) {
      try {
        const cleanKey = key.replace('aura-setting-', '');
        store[cleanKey] = JSON.parse(localStorage.getItem(key)!);
        cache.set(cleanKey, store[cleanKey]);
      } catch {}
    }
  }

  // If authenticated, overlay with Supabase data (source of truth)
  const auth = getAuthState();
  const supabase = getClient();

  if (auth.isAuthenticated && supabase) {
    try {
      const { data, error } = await supabase
        .from('settings')
        .select('section, data')
        .eq('user_id', auth.userId!);

      if (!error && data) {
        for (const row of data) {
          const sectionData = row.data as Record<string, any>;
          for (const [key, value] of Object.entries(sectionData)) {
            store[key] = value;
            cache.set(key, value);
            // Sync to localStorage as backup
            localStorage.setItem(`aura-setting-${key}`, JSON.stringify(value));
          }
        }
      }
    } catch {
      // Remote unavailable — localStorage values stand
    }
  }

  cacheLoaded = true;
  return store;
}

// ============================================================================
// Save
// ============================================================================

/**
 * Save a single setting value.
 * Writes to localStorage immediately, then syncs to Supabase.
 */
export function saveSetting(key: string, value: any): void {
  cache.set(key, value);

  // Always write localStorage (fast, offline-capable)
  localStorage.setItem(`aura-setting-${key}`, JSON.stringify(value));

  // If authenticated, sync to Supabase
  const auth = getAuthState();
  const supabase = getClient();

  if (auth.isAuthenticated && supabase) {
    syncToSupabase(key, value, supabase, auth.userId!).catch(() => {
      // Silently fail — localStorage has the value
    });
  }
}

/**
 * Get a setting value from cache.
 */
export function getSetting(key: string): any {
  if (cache.has(key)) return cache.get(key);

  // Fallback to localStorage
  const stored = localStorage.getItem(`aura-setting-${key}`);
  if (stored !== null) {
    try {
      const value = JSON.parse(stored);
      cache.set(key, value);
      return value;
    } catch {}
  }

  return undefined;
}

// ============================================================================
// Supabase Sync
// ============================================================================

/**
 * Determine which settings section a key belongs to.
 */
function inferSection(key: string): SettingsSection {
  // Support both dot notation (account.username) and dash notation (account-username)
  const prefix = key.split(/[.\-]/)[0];
  switch (prefix) {
    case 'account':
    case 'security':
      return 'account';
    case 'org':
      return 'organization';
    case 'dataset':
      return 'datasets';
    case 'sense':
    case 'validate':
    case 'create':
    case 'activate':
    case 'learn':
    case 'evolve':
    case 'mode':
      return 'modes';
    case 'gov':
    case 'governance':
    case 'audit':
    case 'export':
    case 'notify':
      return 'governance';
    case 'health':
      return 'health';
    default:
      return 'account';
  }
}

async function syncToSupabase(
  key: string,
  value: any,
  supabase: SupabaseClient,
  userId: string
): Promise<void> {
  const section = inferSection(key);

  // Read current section data
  const { data: existing } = await supabase
    .from('settings')
    .select('data')
    .eq('user_id', userId)
    .eq('section', section)
    .maybeSingle();

  const sectionData = existing?.data ?? {};
  sectionData[key] = value;

  // Upsert the section
  await supabase.from('settings').upsert(
    {
      user_id: userId,
      section,
      data: sectionData,
    },
    { onConflict: 'user_id,org_id,section' }
  );
}

// ============================================================================
// Realtime
// ============================================================================

/**
 * Subscribe to settings changes from other devices/tabs.
 */
export function subscribeToChanges(
  onChange: (key: string, value: any) => void
): () => void {
  const auth = getAuthState();
  const supabase = getClient();

  if (!auth.isAuthenticated || !supabase) {
    return () => {};
  }

  realtimeChannel = supabase
    .channel('settings-sync')
    .on(
      'postgres_changes',
      {
        event: 'UPDATE',
        schema: 'public',
        table: 'settings',
        filter: `user_id=eq.${auth.userId!}`,
      },
      (payload: any) => {
        const newData = (payload.new as any).data as Record<string, any>;
        for (const [key, value] of Object.entries(newData)) {
          if (cache.get(key) !== value) {
            cache.set(key, value);
            localStorage.setItem(`aura-setting-${key}`, JSON.stringify(value));
            onChange(key, value);
          }
        }
      }
    )
    .subscribe();

  return () => {
    realtimeChannel?.unsubscribe();
    realtimeChannel = null;
  };
}

// ============================================================================
// Bulk Operations
// ============================================================================

/**
 * Export all settings as a JSON object (for backup/export).
 */
export function exportSettings(): Record<string, any> {
  const result: Record<string, any> = {};
  cache.forEach((value, key) => {
    result[key] = value;
  });
  return result;
}

/**
 * Import settings from a JSON object (for restore/import).
 */
export function importSettings(data: Record<string, any>): void {
  for (const [key, value] of Object.entries(data)) {
    saveSetting(key, value);
  }
}
